import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { CivilAppellateDocumentRow, CivilAppellateWizardDraft } from '../types/civilAppellateCaseRequest';
import { FILE_CATEGORY_CIVIL_APPELLATE } from '../pages/wizard/civilAppellate/civilAppellateConstants';
import * as civilAppellateService from '../services/civilAppellateCaseRequests';
import { isDocumentTypeUuid } from '../utils/documentTypeId';

export const DEFAULT_CIVIL_APPELLATE_DRAFT: CivilAppellateWizardDraft = {
  fileCategory: FILE_CATEGORY_CIVIL_APPELLATE,
  fileCategoryId: '',
  subFileCategoryId: '',
  subFileCategoryName: '',
  applicableAreas: [],
  applicableAreaIds: [],
  instituteType: 'government',
  corporateInstituteId: '',
  corporateInstituteName: '',
  corporateInstituteOther: '',
  corporateInstituteContactName: '',
  corporateInstitutePrefix: '',

  description: '',
  isAppealMatter: 'no',
  originalCourt: '',
  judgmentOrOrderDocuments: [],
  provinceId: '',
  provinceName: '',
  districtId: '',
  districtName: '',
  yourReferenceNumber: '',
  otherExternalReferences: '',
  caseNumber: '',
  hasConnectedMatters: 'no',
  connectedCaseNumber: '',
  connectedAgdFileRefAvailable: false,
  connectedAgdFileRef: '',

  plaintiffName: '',
  defendantName: '',
  isIncorporatedBody: false,
  otherInvolvedInstitutions: '',
  legalOfficerName: '',
  legalOfficerContact: '',

  urgencyLevelId: '',
  urgencyLevelName: '',
  deadline: '',
  urgencyJustification: '',

  deliveryMethodIds: [],
  deliveryMethods: [],
  deliveryOther: '',

  documents: [],
  confirmCorrect: false,

  draftRequestId: '',
  referenceNo: '',
};

export type CivilAppellateWizardContextValue = {
  draft: CivilAppellateWizardDraft;
  setDraft: React.Dispatch<React.SetStateAction<CivilAppellateWizardDraft>>;
  reset: () => void;
};

type WizardSeedState = { seedDraft?: Partial<CivilAppellateWizardDraft> };

export default function CivilAppellateRequestLayout() {
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resumeId = (params.caseRequestId ?? searchParams.get('id') ?? '').trim();
  const [draft, setDraft] = useState<CivilAppellateWizardDraft>({ ...DEFAULT_CIVIL_APPELLATE_DRAFT });
  const [loadingDraft, setLoadingDraft] = useState(Boolean(resumeId));

  useEffect(() => {
    const seed = (location.state as WizardSeedState | null)?.seedDraft;
    if (!seed) return;
    setDraft((d) => ({ ...DEFAULT_CIVIL_APPELLATE_DRAFT, ...d, ...seed }));
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    if (resumeId) return;
    let mounted = true;
    void civilAppellateService.resolveCivilAppellateCategoryId().then((categoryId) => {
      if (!mounted || !categoryId) return;
      setDraft((d) => {
        if (d.fileCategoryId) return d;
        return {
          ...d,
          fileCategoryId: categoryId,
          fileCategory: FILE_CATEGORY_CIVIL_APPELLATE,
        };
      });
    });
    return () => {
      mounted = false;
    };
  }, [resumeId]);

  useEffect(() => {
    if (!resumeId) {
      setLoadingDraft(false);
      return;
    }
    let mounted = true;
    void civilAppellateService.getCivilAppellateRequestDetails(resumeId).then((r) => {
      if (!mounted) return;
      if (r.success && r.data) {
        setDraft({ ...DEFAULT_CIVIL_APPELLATE_DRAFT, ...r.data });
      }
      setLoadingDraft(false);
    });
    return () => {
      mounted = false;
    };
  }, [resumeId]);

  const v = useMemo<CivilAppellateWizardContextValue>(
    () => ({
      draft,
      setDraft,
      reset: () => setDraft({ ...DEFAULT_CIVIL_APPELLATE_DRAFT }),
    }),
    [draft],
  );

  if (loadingDraft) {
    return <div className="pg-sub">Loading case request…</div>;
  }

  return <Outlet context={v} />;
}

function normalizeDocLabel(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDocumentForChecklistItem(
  prev: CivilAppellateDocumentRow[],
  item: { id: string; documentTypeId: string; name: string; documentTypeCode?: string },
): CivilAppellateDocumentRow | undefined {
  return prev.find((d) => {
    if (d.checklistItemId === item.id || d.documentTypeId === item.documentTypeId) return true;
    if (item.documentTypeCode) {
      if (d.documentTypeId === item.documentTypeCode || d.checklistItemId === item.documentTypeCode) return true;
    }
    if (!isDocumentTypeUuid(d.documentTypeId) && normalizeDocLabel(d.documentName) === normalizeDocLabel(item.name)) {
      return true;
    }
    return false;
  });
}

export function syncCivilAppellateDocumentsFromChecklist(
  prev: CivilAppellateDocumentRow[],
  checklist: { id: string; documentTypeId: string; name: string; mandatory: boolean; documentTypeCode?: string }[],
): CivilAppellateDocumentRow[] {
  return checklist.map((item) => {
    const existing = findDocumentForChecklistItem(prev, item);
    if (existing) {
      return {
        ...existing,
        checklistItemId: item.id,
        documentTypeId: item.documentTypeId,
        documentName: item.name,
        mandatory: item.mandatory,
      };
    }
    return {
      id: crypto.randomUUID(),
      checklistItemId: item.id,
      documentTypeId: item.documentTypeId,
      documentName: item.name,
      mandatory: item.mandatory,
      fileName: '',
      originalFileName: '',
      uploadTime: '',
      skipReason: '',
    };
  });
}
